import { useEffect } from 'react'
import { getElementScale } from './element-scale'

const toPixels = (value: number) => `${Math.round(value * 100) / 100}px`

export const useCalculatorSelectBounds = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return

    let frame = 0
    const constrain = () => {
      frame = 0

      document.querySelectorAll<HTMLElement>('[data-calculator-select="true"]').forEach(paper => {
        const listbox = paper.querySelector<HTMLElement>('[role="listbox"]')
        const parent = paper.parentElement
        if (!listbox?.id || !parent) return

        const controlsList = (element: Element) => (
          element.getAttribute('aria-controls')?.split(/\s+/).includes(listbox.id)
        )
        const activeCombobox = document.activeElement?.closest<HTMLElement>('[role="combobox"]')
        const relatedCombobox = Array.from(document.querySelectorAll<HTMLElement>('[role="combobox"]'))
          .find(controlsList)
        const activeControlsList = activeCombobox && controlsList(activeCombobox)
        const combobox = activeControlsList ? activeCombobox : relatedCombobox
        const anchorElement = combobox?.parentElement
        if (!anchorElement || !anchorElement.offsetWidth) return

        const anchor = anchorElement.getBoundingClientRect()
        const parentBounds = parent.getBoundingClientRect()
        const scale = getElementScale(anchorElement)
        const parentScale = getElementScale(parent)
        if (!scale || !parentScale) return

        const ownZoom = String(Math.round(scale / parentScale * 1000000) / 1000000)
        if (paper.style.getPropertyValue('zoom') !== ownZoom) {
          paper.style.setProperty('zoom', ownZoom)
        }

        const computed = getComputedStyle(paper)
        const marginLeft = parseFloat(computed.marginLeft) || 0
        const marginTop = parseFloat(computed.marginTop) || 0
        const marginBottom = parseFloat(computed.marginBottom) || 0
        const availableWidth = Math.max(0, window.innerWidth - 16)
        const width = Math.min(paper.offsetWidth * scale, availableWidth)
        const naturalHeight = Math.max(paper.offsetHeight, paper.scrollHeight) * scale
        const viewportHeight = Math.max(0, window.innerHeight - 16)
        const below = Math.min(viewportHeight, Math.max(0, window.innerHeight - 8 - anchor.bottom - marginTop * scale))
        const above = Math.min(viewportHeight, Math.max(0, anchor.top - 8 - marginBottom * scale))
        const placeAbove = below < naturalHeight && above > below
        const availableHeight = placeAbove ? above : below
        const height = Math.min(naturalHeight, availableHeight)
        const desiredX = anchor.left + (anchor.width - width) / 2
        const desiredY = placeAbove
          ? anchor.top - height - marginBottom * scale
          : anchor.bottom + marginTop * scale
        const physicalX = Math.max(8, Math.min(desiredX, window.innerWidth - 8 - width))
        const physicalY = Math.max(8, Math.min(desiredY, window.innerHeight - 8 - height))
        const parentLeft = parentBounds.left + parent.clientLeft * parentScale
        const parentTop = parentBounds.top + parent.clientTop * parentScale
        const originY = placeAbove ? height / scale : 0
        const nextStyles = {
          left: toPixels((physicalX - parentLeft) / scale - marginLeft),
          top: toPixels((physicalY - parentTop) / scale - marginTop),
          'max-width': toPixels(availableWidth / scale),
          'max-height': toPixels(availableHeight / scale),
          'transform-origin': `${toPixels(width / scale / 2)} ${toPixels(originY)}`,
          ...(parseFloat(computed.minWidth) * scale > availableWidth && {
            'min-width': toPixels(availableWidth / scale),
          }),
        }

        Object.entries(nextStyles).forEach(([ property, value ]) => {
          if (paper.style.getPropertyValue(property) !== value) {
            paper.style.setProperty(property, value)
          }
        })
      })
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(constrain)
    }

    const observer = new MutationObserver(schedule)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [ 'style', 'aria-expanded', 'aria-controls' ],
    })
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    schedule()

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
      cancelAnimationFrame(frame)
    }
  }, [ enabled ])
}
